import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ROUTES } from '../../../routes';

import { API_BASE_URL } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { createPlaceholderUrl } from '../../../utils/PlaceholderCreator';

const OriginalDisplay = ({ characters, cards }) => {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const isUserAdmin = isAdmin();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    const sanitizeFilename = (name) => {
        if (typeof name !== 'string') return '';
        return name.replace(/[\\/*?:"<>|]/g, '');
    };

    const getEarliestDate = (charId) => {
        const charCards = cards.filter(c => c.Id_Personaje === charId);
        const validDates = charCards
            .map(c => c.release_ja)
            .filter(d => d)
            .map(d => new Date(d));
        return validDates.length > 0 ? new Date(Math.min(...validDates)) : null;
    };

    // Filtrar por búsqueda y tipo de jugador (NPC solo para admins)
    let filteredCharacters = characters.filter(char => {
        const matchesSearch = char.name_en.toLowerCase().includes(searchTerm.toLowerCase());
        // Mostrar si es admin O si el personaje NO es NPC (es decir, es PC o undefined)
        const isVisible = isUserAdmin || char.Player_type !== 'NPC';
        return matchesSearch && isVisible;
    });

    // Ordenar personajes
    const sortedCharacters = [...filteredCharacters].sort((a, b) => {
        let comparison = 0;

        if (sortBy === 'name') {
            comparison = a.name_en.localeCompare(b.name_en);
        } else if (sortBy === 'date') {
            const dateA = getEarliestDate(a.Id_Personaje);
            const dateB = getEarliestDate(b.Id_Personaje);
            if (!dateA && !dateB) comparison = 0;
            else if (!dateA) comparison = 1;
            else if (!dateB) comparison = -1;
            else comparison = dateA - dateB;
        }

        return sortOrder === 'asc' ? comparison : -comparison;
    });

    // Sorting is handled inside render

    return (
        <div className="w-full">
            {/* Controles de búsqueda y filtros */}
            <div className="mb-6 space-y-3">
                {/* Barra de búsqueda */}
                <input
                    type="search"
                    placeholder={t('OriginalDisplay.placeholder_search')}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-secondary-blue outline-none"
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                {/* Controles de ordenamiento */}
                <div className="flex gap-2">
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="flex-1 p-2.5 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-secondary-blue focus:border-secondary-blue outline-none"
                    >
                        <option value="name">{t('OriginalDisplay.sort_name')}</option>
                        <option value="date">{t('OriginalDisplay.sort_date')}</option>
                    </select>
                    <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                        title={sortOrder === 'asc' ? t('OriginalDisplay.tooltip_desc') : t('OriginalDisplay.tooltip_asc')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                            {sortOrder === 'asc' ? (
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h5.25m5.25-.75L17.25 9m0 0L21 12.75M17.25 9v12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0l-3.75-3.75M17.25 21L21 17.25" />
                            )}
                        </svg>
                    </button>
                </div>
            </div>

            {/* Admin Actions */}
            {isUserAdmin && (
                <div className="mb-6">
                    <button
                        onClick={() => navigate(`${ROUTES.CHARACTER}/new`)}
                        className="bg-primary-green text-white px-6 py-3 rounded-lg font-bold hover:bg-green-600 transition-colors flex items-center gap-2 shadow-md"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        {t('OriginalDisplay.button_add')}
                    </button>
                </div>
            )}

            {/* Grid de personajes */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {sortedCharacters.map(char => {
                    let thumbnailUrl = createPlaceholderUrl(100, 100, char.name_en.substring(0, 2));

                    const originalCostume = cards.find(c =>
                        c.Id_Personaje === char.Id_Personaje && c.reference === 'Original'
                    );

                    if (originalCostume) {
                        try {
                            const characterName = sanitizeFilename(char.name_en).replace(/ /g, '_');
                            const reference = sanitizeFilename(originalCostume.reference).replace(/ /g, '_');
                            const costumeName = sanitizeFilename(originalCostume.title_en).replace(/ /g, '_');

                            const imageName = `${characterName}_${reference}_${costumeName}.png`;
                            // Optimization: Use API thumb generator + lazy loading
                            thumbnailUrl = `${API_BASE_URL}/api/thumb?img=/images/character/${imageName}&width=200`;
                        } catch (error) {
                            console.error("Could not construct thumbnail URL for:", char.name_en, error);
                        }
                    }

                    const slug = char.name_en.toLowerCase().replace(/ /g, '-');
                    return (
                        <Link
                            key={char.Id_Personaje}
                            to={`${ROUTES.CHARACTER}/${slug}`}
                            state={{ character: char }}
                            className="group relative flex flex-col items-center bg-white dark:bg-card-dark rounded-3xl p-2 border-2 border-slate-200 dark:border-gray-700 hover:border-emerald-400 dark:hover:border-emerald-500 shadow-sm hover:shadow-[0_10px_40px_-10px_rgba(16,185,129,0.3)] transition-all duration-300 cursor-pointer overflow-hidden"
                        >
                            {/* Fondo sutil (Patrón o color suave) */}
                            <div className="absolute inset-0 bg-slate-50/50 dark:bg-gray-800/30 group-hover:bg-white dark:group-hover:bg-gray-700/50 transition-colors duration-300"></div>

                            {/* Botón de Edición (SIEMPRE VISIBLE para admins) */}
                            {isUserAdmin && (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault(); // Stop Link navigation
                                        e.stopPropagation();
                                        navigate(`${ROUTES.CHARACTER}/${char.Id_Personaje}/edit`);
                                    }}
                                    className="absolute top-2 right-2 z-20 p-2 rounded-full bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 text-slate-400 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-400 shadow-sm hover:shadow-md transition-all duration-200"
                                    title={t('OriginalDisplay.tooltip_edit')}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                    </svg>
                                </button>
                            )}

                            {/* Contenedor de Imagen */}
                            <div className="relative w-full aspect-square mb-2 p-3">
                                {/* Círculo decorativo de fondo */}
                                <div className="absolute inset-0 m-3 rounded-full border-2 border-dashed border-slate-300 dark:border-gray-600 group-hover:border-emerald-400/50 dark:group-hover:border-emerald-500/50 group-hover:rotate-12 transition-all duration-500"></div>

                                <img
                                    src={thumbnailUrl}
                                    loading="lazy"
                                    alt={char.name_en}
                                    className="relative z-10 w-full h-full rounded-full object-cover object-top shadow-md ring-4 ring-white dark:ring-gray-700 bg-gray-200/50 dark:bg-gray-800/50 group-hover:scale-105 transition-transform duration-300"
                                    onError={(e) => {
                                        e.target.src = createPlaceholderUrl(100, 100, char.name_en.substring(0, 2));
                                    }}
                                />
                            </div>

                            {/* Nombre del Personaje (Estilo Etiqueta/Placa) */}
                            <div className="relative z-10 w-full px-2 pb-2">
                                <div className="bg-slate-100 dark:bg-gray-700 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/30 rounded-xl py-2 px-1 border border-slate-200 dark:border-gray-600 group-hover:border-emerald-200 dark:group-hover:border-emerald-700 transition-colors duration-300">
                                    <p className={`text-center font-black text-slate-700 dark:text-gray-200 uppercase tracking-wider leading-tight group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors duration-300 ${['Matikanefukukitaru', 'Matikanetannhauser'].includes(char.name_en) ? 'text-[10px]' : 'text-xs'}`}>
                                        {char.name_en}
                                    </p>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </div>

            {/* Mensaje si no hay resultados */}
            {sortedCharacters.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">{t('OriginalDisplay.empty_msg')}</p>
                </div>
            )}
        </div>
    );
};

export default OriginalDisplay;
// Revisado: 2025-12-18 [I18n Scan]
